import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

// MC-57: drag-and-drop reordering of task cards within a single stage.
// Attached by ColumnComponent only when the stage sorts by `position`
// (other sort keys derive the order from a different column, so a
// manual drop would be silently overwritten on the next render).
//
// On drop, posts the new id sequence to `reorder_tasks_space_team_workflow_board_stage_path`.
// The endpoint replies 204 on success; we leave the DOM where the
// drop placed it because the optimistic order already matches the
// server's. Failures roll back the move locally so the visible
// order doesn't drift from the server's.
//
// MC-175: drag-and-drop is desktop-only. MC-136 explicitly scoped DnD
// out on mobile — touch drags conflict with vertical scroll on a
// position-sorted column, leaving the user unable to scroll without
// accidentally reordering. The controller now binds Sortable only at
// `md+`; on viewport changes it tears down or re-initialises so
// rotating a tablet doesn't strand a stale instance.
export default class extends Controller {
  static values = { url: String, stageId: Number }

  static DESKTOP_QUERY = "(min-width: 768px)"

  connect() {
    this.mql = window.matchMedia(this.constructor.DESKTOP_QUERY)
    this.mqlHandler = this.onViewportChange.bind(this)
    this.mql.addEventListener("change", this.mqlHandler)
    if (this.mql.matches) this.activate()
  }

  disconnect() {
    if (this.mql) this.mql.removeEventListener("change", this.mqlHandler)
    this.deactivate()
  }

  onViewportChange(event) {
    if (event.matches) {
      this.activate()
    } else {
      this.deactivate()
    }
  }

  activate() {
    if (this.sortable) return
    this.sortable = Sortable.create(this.element, {
      animation: 150,
      ghostClass: "card-task--ghost",
      // Each card renders with Rails' `dom_id(task)` (e.g. `task_42`);
      // telling Sortable to key on `id` lets `toArray()` return those
      // ids in the new visual order without any extra wiring.
      dataIdAttr: "id",
      onStart: () => { this.previousOrder = this.sortable.toArray() },
      onEnd: this.persist.bind(this)
    })
    this.previousOrder = this.sortable.toArray()
  }

  deactivate() {
    if (this.sortable) {
      this.sortable.destroy()
      this.sortable = null
    }
  }

  async persist() {
    const ids = this.collectTaskIds()
    const response = await fetch(this.urlValue, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": this.csrfToken()
      },
      body: JSON.stringify({ task_ids: ids })
    })

    if (!response.ok) {
      // Server rejected the new order — revert the DOM so what the
      // operator sees matches what the server holds.
      this.sortable.sort(this.previousOrder, true)
    } else {
      this.previousOrder = this.sortable.toArray()
    }
  }

  collectTaskIds() {
    // Rails' `dom_id(task)` renders ids in the form `task_<id>`.
    // Sortable returns the same ids in their new visual order via
    // `toArray()`, so we just strip the prefix.
    return this.sortable.toArray().map((domId) => {
      const match = domId.match(/^task_(\d+)$/)
      return match ? Number(match[1]) : null
    }).filter((id) => id !== null)
  }

  csrfToken() {
    const meta = document.querySelector("meta[name='csrf-token']")
    return meta ? meta.getAttribute("content") : ""
  }
}
